// Module: passphrase
// Contract: ./contract.md   Spec: docs/vault-spec-v2.md §3.2
//
// Generates and validates diceware passphrases, and normalises them so a phrase typed at unlock
// derives the same key as the phrase generated at enrolment.

import { WORDLIST, WORDLIST_SIZE } from "./wordlist.js"

export const MIN_WORDS = 5
export const DEFAULT_WORDS = 6
/** Floor for a user-chosen password. Not a strength score — just a refusal to accept the worst. */
export const MIN_PASSWORD_LENGTH = 12
export { WORDLIST_SIZE }

/** Fills `out` with random 16-bit values. Injected so sampling can be tested deterministically. */
export type RandomSource = (out: Uint16Array) => void

export type Validation =
  | { valid: true; wordCount: number; entropyBits: number }
  | { valid: false; reason: "empty" | "too-few-words" | "unknown-word"; badWord?: string }

export type FloorResult =
  | { ok: true }
  | { ok: false; reason: "empty" | "too-short" | "too-common" }

/**
 * A hundred or so of the passwords that appear at the top of every breach corpus. Not a strength
 * estimator and not a substitute for one — just a refusal to accept the handful of values an
 * attacker would try first.
 */
const COMMON = new Set(
  ("password passw0rd password1 password123 123456 12345678 123456789 1234567890 qwerty qwerty123 " +
    "letmein welcome monkey dragon baseball football iloveyou trustno1 sunshine princess admin " +
    "administrator abc123 111111 123123 000000 iloveyou1 superman batman starwars whatever " +
    "computer michael jennifer jordan hunter2 secret master shadow ashley bailey passwort " +
    "changeme default guest login root user test hello freedom summer winter spring autumn " +
    "chocolate cookie flower orange purple yellow soccer hockey tennis golden silver diamond")
    .split(" "),
)

/** The gate for a user-chosen password. Case-sensitive, unlike diceware validation. */
export function meetsFloor(password: string): FloorResult {
  const normalized = normalize(password)
  if (normalized.length === 0) return { ok: false, reason: "empty" }
  if (normalized.length < MIN_PASSWORD_LENGTH) return { ok: false, reason: "too-short" }
  if (COMMON.has(normalized.toLowerCase().replace(/\s+/gu, ""))) {
    return { ok: false, reason: "too-common" }
  }
  return { ok: true }
}

const defaultRng: RandomSource = (out) => {
  crypto.getRandomValues(out)
}

// Largest multiple of WORDLIST_SIZE that fits in a Uint16. Draws at or above this are discarded:
// taking `value % 7776` directly would make the first 3328 words measurably likelier, which is a
// silent loss of the entropy this module exists to guarantee.
const REJECTION_THRESHOLD = Math.floor(65536 / WORDLIST_SIZE) * WORDLIST_SIZE

const wordSet: ReadonlySet<string> = new Set(WORDLIST)

/**
 * Draws one uniformly distributed index in [0, WORDLIST_SIZE) using rejection sampling.
 * Requests randomness in small batches rather than one value at a time.
 */
function drawIndex(rng: RandomSource, buf: Uint16Array, state: { pos: number }): number {
  for (;;) {
    if (state.pos >= buf.length) {
      rng(buf)
      state.pos = 0
    }
    const value = buf[state.pos]!
    state.pos += 1
    if (value < REJECTION_THRESHOLD) return value % WORDLIST_SIZE
  }
}

export function generate(opts: { words?: number; rng?: RandomSource } = {}): string {
  const words = opts.words ?? DEFAULT_WORDS
  if (!Number.isInteger(words) || words < MIN_WORDS) {
    throw new RangeError(`passphrase must be at least ${MIN_WORDS} words, got ${words}`)
  }

  const rng = opts.rng ?? defaultRng
  const buf = new Uint16Array(words * 2)
  const state = { pos: buf.length } // forces an initial fill
  const out: string[] = []
  for (let i = 0; i < words; i += 1) out.push(WORDLIST[drawIndex(rng, buf, state)]!)
  return out.join(" ")
}

/**
 * Canonical form, applied before any key derivation. Two deliberate concessions to how people
 * actually type, each costing a little entropy and buying a lot of usability.
 *
 * **All whitespace is removed.** A phone that turns a newline into a space, a user unsure whether
 * the words were spaced, a value pasted from a note with a line break — all now derive the same
 * key. The cost is that "my dog" and "mydog" collide, which is a rounding error next to a vault
 * that will not open.
 *
 * **Only the first character is lowercased.** iOS capitalises the first letter of a text field and
 * a desktop keyboard does not, so the same password typed on a phone and a laptop would otherwise
 * be two different passwords. Later capitals are preserved, so the entropy of a mid-word capital
 * survives; only the one character the keyboard decides for you is folded.
 */
export function normalize(phrase: string): string {
  const stripped = phrase.normalize("NFKC").replace(/\s+/gu, "")
  if (stripped.length === 0) return stripped
  return stripped[0]!.toLowerCase() + stripped.slice(1)
}

export function entropyBits(wordCount: number): number {
  return wordCount * Math.log2(WORDLIST_SIZE)
}

/**
 * Enforcement point for spec §3.2: the application generates passphrases and refuses user-chosen
 * ones. `key-hierarchy` calls this before derivation so the rule holds at the cryptographic
 * boundary, not only in the UI.
 */
export function validate(phrase: string): Validation {
  // Its own normalisation: `normalize()` strips whitespace entirely, which is right for deriving
  // a key and useless for counting words. Diceware is lowercase by definition, so folding case
  // here is safe.
  const normalized = phrase.normalize("NFKC").toLowerCase().trim().replace(/\s+/gu, " ")
  if (normalized.length === 0) return { valid: false, reason: "empty" }

  const words = normalized.split(" ")
  if (words.length < MIN_WORDS) return { valid: false, reason: "too-few-words" }

  for (const word of words) {
    if (!wordSet.has(word)) return { valid: false, reason: "unknown-word", badWord: word }
  }

  return { valid: true, wordCount: words.length, entropyBits: entropyBits(words.length) }
}
